import { useState, useEffect, useCallback } from 'react'
import { getInviteeList } from '../services/inviteeService'
import type { Invitee, InviteeFilters, InviteeListResponse } from '../types/invitee'

const SESSION_STORAGE_KEY = 'invitereportlist_filter'
const SESSION_META_KEYS = ['org_name', 'company_name', 'property_name'] as const
type SessionMetaKey = (typeof SESSION_META_KEYS)[number]
type InviteeFiltersSession = InviteeFilters & Partial<Record<SessionMetaKey, string>>

/**
* Hook for managing invitee list data and filters
* @param inviteCodeFromUrl - when present (e.g. ?invite_code=xyz), list is filtered to that invite so respective data is shown
*/
export function useInviteeList(inviteCodeFromUrl?: string | null) {
  const [invitees, setInvitees] = useState<Invitee[]>([])
  const [loading, setLoading] = useState(false)
  const [sorting, setSorting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [currentFilters, setCurrentFilters] = useState<InviteeFilters | null>(null)

  const defaultFilters: InviteeFilters = {
    page: 1,
    perPage: 15,
    sortBy: 'desc',
    orderBy: 'invite_status_date',
  }

  const loadFiltersFromSession = useCallback((): InviteeFilters => {
    try {
      const saved = sessionStorage.getItem(SESSION_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved) as InviteeFiltersSession
        // Strip any meta keys before using as API/URL filters
        SESSION_META_KEYS.forEach((k) => {
          delete (parsed as any)[k]
        })
        return parsed
      }
    } catch (e) {
      console.error('Failed to load filters from session:', e)
    }
    return defaultFilters
  }, [])

  // Save filters to session storage
  const saveFiltersToSession = useCallback((filters: InviteeFilters, meta?: Partial<Record<SessionMetaKey, string>>) => {
    try {
      const payload: InviteeFiltersSession = { ...(filters as InviteeFiltersSession), ...(meta || {}) }
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {
      console.error('Failed to save filters to session:', e)
    }
  }, [])

  // Clear filters from session storage
  const clearFiltersFromSession = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    } catch (e) {
      console.error('Failed to clear filters from session:', e)
    }
  }, [])

  // Initialize filters from Session Storage (primary) or Defaults
  const getInitialFilters = useCallback((): InviteeFilters => {
    // 1. Try to load from session storage (Primary source for "old app" behavior)
    try {
      const sessionFilters = loadFiltersFromSession()
      // If we have valid session filters, use them
      if (sessionFilters && Object.keys(sessionFilters).length > 0) {
        // Merge with defaults to ensure all required fields exist
        return { ...defaultFilters, ...sessionFilters }
      }
    } catch (e) {
      console.error('Error loading filters', e)
    }

    // 2. Fallback to defaults
    return defaultFilters
  }, [loadFiltersFromSession])

  // Fetch invitees (with loading indicator)
  const fetchInvitees = useCallback(async (filters: InviteeFilters, showLoading = true) => {
    if (showLoading) {
      setLoading(true)
    }
    setError(null)

    try {
      const response: InviteeListResponse = await getInviteeList(filters)
      setInvitees(response.data || [])
      setPagination({
        current: response.current || 1,
        last: response.last || 1,
        total: response.total || 0,
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch invitees'
      setError(errorMessage)
      setInvitees([])
    } finally {
      if (showLoading) {
        setLoading(false)
      }
      setSorting(false)
    }
  }, [])

  // Apply filters
  const applyFilters = useCallback(
    (newFilters: Partial<InviteeFilters>, meta?: Partial<Record<SessionMetaKey, string>>) => {
      const baseFilters = currentFilters || getInitialFilters()

      // Check if only page is being changed (no other filters)
      const isOnlyPageChange = Object.keys(newFilters).length === 1 && 'page' in newFilters

      // Determine page value:
      // - If explicitly provided in newFilters, use it
      // - If only page is changing, use the new page value
      // - Otherwise (other filters changing), reset to page 1
      const pageValue = newFilters.page !== undefined
        ? newFilters.page
        : (isOnlyPageChange ? baseFilters.page : 1)

      const updatedFilters: InviteeFilters = {
        ...baseFilters,
        ...newFilters,
        page: pageValue,
      }

      // Update internal state
      setCurrentFilters(updatedFilters)

      // Save to session storage (Primary storage)
      saveFiltersToSession(updatedFilters, meta)

      // Fetch data
      fetchInvitees(updatedFilters)
    },
    [currentFilters, getInitialFilters, saveFiltersToSession, fetchInvitees]
  )

  // Reset filters
  const resetFilters = useCallback(() => {
    clearFiltersFromSession()
    setCurrentFilters(defaultFilters)
    // No URL update needed
    fetchInvitees(defaultFilters)
  }, [clearFiltersFromSession, fetchInvitees])

  // Change page
  const changePage = useCallback(
    (page: number) => {
      applyFilters({ page })
    },
    [getInitialFilters, applyFilters]
  )

  // Change per page
  const changePerPage = useCallback(
    (perPage: number) => {
      applyFilters({ perPage, page: 1 })
    },
    [applyFilters]
  )

  // Sort
  const sort = useCallback(
    (orderBy: string) => {
      const baseFilters = currentFilters || getInitialFilters()
      // Match Angular reports-invites: every header click toggles direction, then applies the clicked column
      const newSortBy = baseFilters.sortBy === 'asc' ? 'desc' : 'asc'

      // Create updated filters with new sort params
      const updatedFilters: InviteeFilters = {
        ...baseFilters,
        orderBy,
        sortBy: newSortBy,
      }

      // Update internal state (for UI to reflect sort changes)
      setCurrentFilters(updatedFilters)
      setSorting(true)

      // Save to session storage
      saveFiltersToSession(updatedFilters)

      // Show the same loading state used by filters while the sorted list reloads.
      fetchInvitees(updatedFilters)
    },
    [currentFilters, getInitialFilters, saveFiltersToSession, fetchInvitees]
  )

  // When invite_code is in URL: filter by it so respective data is shown. When not in URL: show full list.
  useEffect(() => {
    const base = getInitialFilters()
    const filters: InviteeFilters = inviteCodeFromUrl
      ? { ...base, q: inviteCodeFromUrl, page: 1 }
      : { ...base, q: undefined, page: 1 }
    setCurrentFilters(filters)
    saveFiltersToSession(filters)
    fetchInvitees(filters)
  }, [inviteCodeFromUrl])

  // Get the effective filters (internal state takes precedence)
  const getEffectiveFilters = useCallback((): InviteeFilters => {
    return currentFilters || getInitialFilters()
  }, [currentFilters, getInitialFilters])

  return {
    invitees,
    loading,
    sorting,
    error,
    pagination,
    filters: getEffectiveFilters(),
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch: () => {
      const filters = getEffectiveFilters()
      fetchInvitees(filters)
    },
  }
}
